/**
 * FAMILY PRODUCT GRID - McMaster-Carr Style v4.0
 * 
 * Displays products in sortable/filterable table with inline add-to-cart.
 * 
 * v4.0 CHANGE (Family Widget Migration): Product data is no longer embedded
 * by Liquid at render time. Instead, it's fetched client-side from:
 *   /collections/all/family-{code}?view=family-json
 * This avoids Shopify's 50-item default array limit that applied when
 * filtering collections.all.products by tag directly in Liquid. The fetch
 * target is a real collection render (tag-filtered), so it paginates
 * properly (250/page) and supports more pages for very large families.
 * 
 * Updated: 2026-06-18
 */

(function() {
  'use strict';

  // Get config (family code, fetch URL, current product info) from Liquid
  const dataScript = document.getElementById('family-products-data');
  if (!dataScript) {
    console.warn('[FamilyGrid] No config data found');
    return;
  }

  let familyData;
  try {
    familyData = JSON.parse(dataScript.textContent);
  } catch (e) {
    console.error('[FamilyGrid] Failed to parse config data:', e);
    return;
  }

  const config = familyData.config;
  let allProducts = [];

  const grid = document.getElementById('family-grid-table');
  const tbody = document.getElementById('family-grid-body');
  const filtersContainer = document.getElementById('family-filters');
  const itemsSelectedLabel = document.querySelector('.items-selected');
  const bulkAddBtn = document.getElementById('bulk-add-cart');

  if (!grid || !tbody) return;

  // Get column configuration
  const ColumnConfig = window.FamilyGridColumns || { default: [], getColumnsForType: () => [] };
  let columns = ColumnConfig.default;

  // State
  let currentSort = { field: 'diameter', direction: 'asc' };
  let activeFilters = {};

  // =============================================
  // FETCH PRODUCT DATA
  // =============================================

  function showError(message) {
    tbody.innerHTML = `
      <tr class="no-results">
        <td colspan="12" style="text-align: center; padding: 2rem; color: #6b7280;">
          ${message}
        </td>
      </tr>
    `;
  }

  async function fetchAllPages(baseUrl) {
    const products = [];
    let page = 1;
    let totalPages = 1;

    do {
      const url = page === 1 ? baseUrl : `${baseUrl}&page=${page}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Fetch failed with status ${res.status}`);
      }
      const data = await res.json();
      products.push(...(data.products || []));
      totalPages = data.totalPages || 1;
      page++;
    } while (page <= totalPages);

    return products;
  }

  async function loadFamilyProducts() {
    if (!config.familyFetchUrl) {
      console.warn('[FamilyGrid] No fetch URL in config');
      showError('Unable to load family products.');
      return;
    }

    try {
      allProducts = await fetchAllPages(config.familyFetchUrl);

      // Mark current product
      allProducts.forEach(p => {
        p.isCurrent = p.handle === config.currentProductHandle;
      });

      console.log('[FamilyGrid] Loaded', allProducts.length, 'product rows from', config.familyFetchUrl);

      if (allProducts.length === 0) {
        showError('No products found for this family.');
        return;
      }

      renderTable();
    } catch (err) {
      console.error('[FamilyGrid] Failed to load family products:', err);
      showError('Failed to load family products. Please refresh the page.');
    }
  }

  // =============================================
  // RENDER TABLE
  // =============================================

  function renderTable() {
    let products = [...allProducts].filter(p => p !== null);
    
    // Determine columns based on product type + dynamic finish handling
    if (products.length > 0 && ColumnConfig.getColumnsForType) {
      const productType = config.familyName || config.productType || '';
      
      // Check if we should hide finish (all products are stainless)
      const hideFinish = ColumnConfig.shouldHideFinish ? ColumnConfig.shouldHideFinish(products) : false;
      
      columns = ColumnConfig.getColumnsForType(productType, { hideFinish });
      
      console.log('[FamilyGrid] Product type:', productType, '| Hide finish:', hideFinish, '| Columns:', columns.length);
    }
    
// Build table header dynamically
renderTableHeader();

// Build filter dropdowns only once
if (!filtersContainer.dataset.initialized) {
    renderFilterControls();
    filtersContainer.dataset.initialized = "true";
}
    
    // Apply filters
    products = applyFilters(products);
    
    // Apply sort
    products = applySort(products);
    
    // Clear and rebuild tbody
    tbody.innerHTML = '';
    
    const colCount = columns.length + 3; // +3 for select checkbox, qty, and cart columns
    
    if (products.length === 0) {
      tbody.innerHTML = `
        <tr class="no-results">
          <td colspan="${colCount}" style="text-align: center; padding: 2rem; color: #6b7280;">
            No products match your filters. <button type="button" onclick="document.getElementById('clear-filters').click()" style="color: #2563eb; background: none; border: none; cursor: pointer; text-decoration: underline;">Clear filters</button>
          </td>
        </tr>
      `;
      return;
    }
    
    products.forEach(product => {
      const row = createProductRow(product);
      tbody.appendChild(row);
    });
    
    // Update filter dropdowns with available values
    updateFilterOptions(products);
  }
  
  function renderTableHeader() {
    const thead = grid.querySelector('thead');
    if (!thead) return;
    
    let headerHtml = '<tr>';

    // Bulk-select checkbox column
    headerHtml += '<th class="col-select"><input type="checkbox" id="select-all-checkbox" aria-label="Select all"></th>';

    // Dynamic spec columns
    columns.forEach(col => {
      const sortClass = col.sortable ? 'sortable' : '';
      const alignClass = col.align ? `col-${col.align}` : '';
      const sortIcon = col.sortable ? '<span class="sort-icon"></span>' : '';
      headerHtml += `<th class="${sortClass} ${alignClass}" data-sort="${col.key}">${col.label}${sortIcon}</th>`;
    });
    
    // Fixed columns: Qty and Cart
    headerHtml += '<th class="col-qty">Qty</th>';
    headerHtml += '<th class="col-cart"></th>';
    headerHtml += '</tr>';
    
    thead.innerHTML = headerHtml;

    // Select-all checkbox toggles every row checkbox currently in the table
    const selectAllBox = document.getElementById('select-all-checkbox');
    if (selectAllBox) {
      selectAllBox.addEventListener('change', () => {
        const checked = selectAllBox.checked;
        tbody.querySelectorAll('.row-select-checkbox:not(:disabled)').forEach(cb => {
          cb.checked = checked;
        });
        updateBulkSelectionState();
      });
    }

    // Re-bind sort listeners
    thead.querySelectorAll('th.sortable').forEach(th => {
      th.addEventListener('click', () => {
        const field = th.dataset.sort;
        if (currentSort.field === field) {
          currentSort.direction = currentSort.direction === 'asc' ? 'desc' : 'asc';
        } else {
          currentSort = { field, direction: 'asc' };
        }
        thead.querySelectorAll('th.sortable').forEach(h => {
          h.classList.remove('sorted-asc', 'sorted-desc');
        });
        th.classList.add(`sorted-${currentSort.direction}`);
        renderTable();
      });
    });
  }
  
  function renderFilterControls() {
    if (!filtersContainer) return;
    
    // Get filterable columns
    const filterableCols = columns.filter(c => c.filterable);
    
    let html = '';
    filterableCols.forEach(col => {
      html += `
        <div class="filter-group">
          <label for="filter-${col.key}">${col.label}</label>
          <select id="filter-${col.key}" data-filter="${col.key}">
            <option value="">All</option>
          </select>
        </div>
      `;
    });
    
    html += '<button type="button" class="filter-clear" id="clear-filters">Clear Filters</button>';
    
    filtersContainer.innerHTML = html;
    
    // Bind filter events
    filtersContainer.querySelectorAll('select').forEach(select => {
      select.addEventListener('change', (e) => {
        const field = e.target.dataset.filter;
        const value = e.target.value;
        if (value) {
          activeFilters[field] = value;
        } else {
          delete activeFilters[field];
        }
        renderTable();
      });
    });
    
    document.getElementById('clear-filters')?.addEventListener('click', () => {
      activeFilters = {};
      filtersContainer.querySelectorAll('select').forEach(s => s.value = '');
      renderTable();
    });
  }

  function createProductRow(product) {
    const tr = document.createElement('tr');
    tr.dataset.productId = product.id;
    tr.dataset.variantId = product.variantId;
    
    // Store all filterable/sortable values as data attributes
    columns.forEach(col => {
      if (col.sortable || col.filterable) {
        tr.dataset[col.key] = product[col.key] || '';
      }
    });
    
    // Mark current product
    if (product.handle === config.currentProductHandle) {
      tr.classList.add('current-product');
    }
    
    let rowHtml = '';

    // Bulk-select checkbox
    rowHtml += `
      <td class="col-select">
        <input type="checkbox" class="row-select-checkbox" data-variant-id="${product.variantId}" ${!product.available ? 'disabled' : ''}>
      </td>
    `;

    // Build cells based on column config
    columns.forEach((col, idx) => {
      let value = product[col.key];
      let cellClass = col.align ? `col-${col.align}` : '';
      
      // Format value based on column type
      if (col.format === 'money' && value) {
        value = formatMoney(parseFloat(value));
      } else if (value === null || value === undefined || value === '') {
        value = '-';
      } else {
        value = escapeHtml(String(value));
      }
      
      // First column gets the product link
      if (idx === 0) {
        rowHtml += `<td class="${cellClass}"><a href="/products/${product.handle}" class="product-link">${value}</a></td>`;
      } else {
        rowHtml += `<td class="${cellClass}">${value}</td>`;
      }
    });
    
    // Qty input
    rowHtml += `
      <td class="col-qty">
        <input type="number" class="qty-input" value="1" min="1" max="9999" data-variant-id="${product.variantId}" ${!product.available ? 'disabled' : ''}>
      </td>
    `;
    
    // Add to cart button
    rowHtml += `
      <td class="col-cart">
        <button type="button" class="btn-add-row" data-variant-id="${product.variantId}" ${!product.available ? 'disabled' : ''}>
          ${product.available ? 'Add' : 'Out'}
        </button>
      </td>
    `;
    
    tr.innerHTML = rowHtml;
    return tr;
  }

  // =============================================
  // FILTERING
  // =============================================

  function applyFilters(products) {
    return products.filter(p => {
      for (const [field, value] of Object.entries(activeFilters)) {
        if (value && String(p[field]) !== String(value)) {
          return false;
        }
      }
      return true;
    });
  }

  function updateFilterOptions(products) {
    const filterableCols = columns.filter(c => c.filterable);
    
    filterableCols.forEach(col => {
      const select = document.querySelector(`[data-filter="${col.key}"]`);
      if (!select) return;
      
      // Get unique values from ALL products (not just filtered)
      const values = [...new Set(allProducts.map(p => p[col.key]).filter(v => v && v !== '-' && v !== null))];
      
      // Sort appropriately
      values.sort((a, b) => {
        const numA = parseFraction(a);
        const numB = parseFraction(b);
        if (numA !== null && numB !== null) return numA - numB;
        return String(a).localeCompare(String(b));
      });
      
      // Preserve current selection
      const currentValue = select.value;
      
      // Rebuild options
      select.innerHTML = '<option value="">All</option>';
      values.forEach(v => {
        const opt = document.createElement('option');
        opt.value = v;
        opt.textContent = v;
        if (v === currentValue) opt.selected = true;
        select.appendChild(opt);
      });
    });
  }

  // =============================================
  // SORTING
  // =============================================

  function applySort(products) {
    const { field, direction } = currentSort;
    
    return products.sort((a, b) => {
      // Primary sort by selected field
      let result = compareValues(a[field], b[field]);
      
      // If same, secondary sort to keep variants grouped
      if (result === 0 && field === 'diameter') {
        // Same size → sort by length
        result = compareValues(a.length, b.length);
      }
      if (result === 0) {
        // Same size+length → sort by product ID (keeps variants together)
        result = (a.id || 0) - (b.id || 0);
      }
      if (result === 0) {
        // Same product → sort by pack size (larger packs first)
        const packA = extractPackQty(a.packQty);
        const packB = extractPackQty(b.packQty);
        result = packB - packA; // Descending - larger pack first
      }
      
      return direction === 'asc' ? result : -result;
    });
  }
  
  function compareValues(valA, valB) {
    // Handle nulls
    if (valA == null && valB == null) return 0;
    if (valA == null) return 1;
    if (valB == null) return -1;
    
    // Try numeric/fraction sort
    const numA = parseFraction(valA);
    const numB = parseFraction(valB);
    
    if (numA !== null && numB !== null) {
      return numA - numB;
    }
    return String(valA).localeCompare(String(valB), undefined, { numeric: true });
  }
  
  function extractPackQty(packStr) {
    if (!packStr) return 0;
    // Extract number from strings like "Bulk Box (100 pcs)" or "Package (10 pcs)"
    const match = String(packStr).match(/\(?([\d,]+)\s*pcs?\)?/i);
    if (match) {
      return parseInt(match[1].replace(/,/g, '')) || 0;
    }
    return parseInt(packStr) || 0;
  }

  function parseFraction(str) {
    if (str == null) return null;
    str = String(str).trim();
    
    // Handle "#8" style sizes
    if (str.startsWith('#')) {
      const num = parseFloat(str.slice(1));
      return isNaN(num) ? null : num - 100; // Make # sizes sort before fractions
    }
    
    // Handle fractions like "1/4" or "1-1/2"
    const fractionMatch = str.match(/^(\d+)?[-\s]?(\d+)\/(\d+)/);
    if (fractionMatch) {
      const whole = fractionMatch[1] ? parseInt(fractionMatch[1]) : 0;
      const num = parseInt(fractionMatch[2]);
      const denom = parseInt(fractionMatch[3]);
      return whole + (num / denom);
    }
    
    // Handle decimal
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  }

  // =============================================
  // BULK SELECT + BULK ADD TO CART
  // =============================================

  function updateBulkSelectionState() {
    const checkedBoxes = tbody.querySelectorAll('.row-select-checkbox:checked');
    const count = checkedBoxes.length;

    if (itemsSelectedLabel) {
      itemsSelectedLabel.textContent = `${count} item${count === 1 ? '' : 's'} selected`;
    }
    if (bulkAddBtn) {
      bulkAddBtn.disabled = count === 0;
    }

    // Keep select-all checkbox in sync (checked / indeterminate / unchecked)
    const selectAllBox = document.getElementById('select-all-checkbox');
    if (selectAllBox) {
      const allBoxes = tbody.querySelectorAll('.row-select-checkbox:not(:disabled)');
      selectAllBox.checked = allBoxes.length > 0 && count === allBoxes.length;
      selectAllBox.indeterminate = count > 0 && count < allBoxes.length;
    }
  }

  function handleRowCheckboxChange(e) {
    if (!e.target.classList.contains('row-select-checkbox')) return;
    updateBulkSelectionState();
  }

  async function handleBulkAddToCart() {
    const checkedBoxes = Array.from(tbody.querySelectorAll('.row-select-checkbox:checked'));
    if (checkedBoxes.length === 0) return;

    const items = checkedBoxes.map(cb => {
      const row = cb.closest('tr');
      const qtyInput = row.querySelector('.qty-input');
      const qty = parseInt(qtyInput?.value) || 1;
      return { id: parseInt(cb.dataset.variantId), quantity: qty };
    });

    const originalText = bulkAddBtn.textContent;
    bulkAddBtn.disabled = true;
    bulkAddBtn.textContent = 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        throw new Error(`Bulk add failed with status ${res.status}`);
      }

      bulkAddBtn.textContent = 'Added!';
      updateCartCount();

      // Clear selections after a successful add
      checkedBoxes.forEach(cb => { cb.checked = false; });
      updateBulkSelectionState();

      setTimeout(() => {
        bulkAddBtn.textContent = originalText;
      }, 2000);
    } catch (err) {
      console.error('[FamilyGrid] Bulk add to cart failed:', err);
      bulkAddBtn.textContent = 'Error — try again';
      setTimeout(() => {
        bulkAddBtn.textContent = originalText;
        bulkAddBtn.disabled = checkedBoxes.length === 0;
      }, 2500);
    }
  }

  // =============================================
  // ADD TO CART
  // =============================================

  function handleAddToCart(e) {
    if (!e.target.classList.contains('btn-add-row')) return;
    
    const btn = e.target;
    const variantId = btn.dataset.variantId;
    const row = btn.closest('tr');
    const qtyInput = row.querySelector('.qty-input');
    const qty = parseInt(qtyInput?.value) || 1;
    
    btn.classList.add('adding');
    btn.textContent = '...';
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId), quantity: qty }]
      })
    })
    .then(res => {
      if (res.ok) {
        btn.classList.remove('adding');
        btn.classList.add('added');
        btn.textContent = '✓';
        
        // Update cart count in header if possible
        updateCartCount();
        
        setTimeout(() => {
          btn.classList.remove('added');
          btn.textContent = 'Add';
        }, 2000);
      } else {
        throw new Error('Failed to add');
      }
    })
    .catch(err => {
      btn.classList.remove('adding');
      btn.textContent = 'Error';
      setTimeout(() => { btn.textContent = 'Add'; }, 2000);
    });
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const countEls = document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble');
        countEls.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) el.classList.remove('hidden');
        });
      })
      .catch(() => {});
  }

  // =============================================
  // HELPERS
  // =============================================

  function formatMoney(cents) {
    // Format as USD - adjust based on shop.money_format if needed
    const dollars = (cents || 0);
    return '$' + dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function escapeHtml(str) {
    if (str == null) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // =============================================
  // INIT
  // =============================================

  // Bind cart handlers
  tbody.addEventListener('click', handleAddToCart);
  tbody.addEventListener('change', handleRowCheckboxChange);
  if (bulkAddBtn) {
    bulkAddBtn.addEventListener('click', handleBulkAddToCart);
  }

  // Fetch product data, then render
  loadFamilyProducts();

})();